// src/pages/ConnectorFields.jsx
//
// Field mapping page for a single connector.
// Customers customize display_name, is_visible, and ordering for each field.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  EyeOff,
  Eye,
  Loader2,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { Input } from '@/components/ui/input.jsx';
import {
  TableWrapper,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table.jsx';
import { EmptyState } from '@/components/ui/empty-state.jsx';
import { apiCall } from '../utils/api';
import { canEditConnector } from '../utils/roles';

export default function ConnectorFields() {
  const { id: configId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fields, setFields] = useState([]);
  const [byEntity, setByEntity] = useState({});
  const [discovering, setDiscovering] = useState(false);
  const [busyBulk, setBusyBulk] = useState(false);
  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState({}); // { fieldId: localDisplayName }
  const [searchQuery, setSearchQuery] = useState('');

  const showToast = useCallback((message, kind = 'success') => {
    setToast({ message, kind });
    setTimeout(() => setToast(null), 2400);
  }, []);

  const loadFields = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall(`/connector-fields/configs/${configId}/fields`);
      if (!res.ok) {
        throw new Error(`Failed to load fields (HTTP ${res.status})`);
      }
      const data = await res.json();
      setFields(data.fields || []);
      setByEntity(data.by_entity || {});
    } catch (err) {
      console.error('Failed to load fields:', err);
      setError(err.message || 'Failed to load fields');
    } finally {
      setLoading(false);
    }
  }, [configId]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  async function handleDiscover() {
    setDiscovering(true);
    try {
      const res = await apiCall(
        `/connector-fields/configs/${configId}/discover`,
        { method: 'POST' }
      );
      const data = await res.json();
      if (data.error) {
        showToast(data.error, 'error');
      } else {
        showToast(
          `Discovered ${data.discovered}, inserted ${data.inserted}, updated ${data.updated}`
        );
        await loadFields();
      }
    } catch (err) {
      console.error('Discovery failed:', err);
      showToast(err.message || 'Discovery failed', 'error');
    } finally {
      setDiscovering(false);
    }
  }

  async function updateField(fieldId, patch) {
    try {
      const res = await apiCall(`/connector-fields/${fieldId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const updated = await res.json();
      setFields((prev) => prev.map((f) => (f.id === fieldId ? updated : f)));
      setByEntity((prev) => {
        const next = { ...prev };
        for (const ent of Object.keys(next)) {
          next[ent] = next[ent].map((f) => (f.id === fieldId ? updated : f));
        }
        return next;
      });
    } catch (err) {
      console.error('Update failed:', err);
      showToast('Save failed: ' + (err.message || 'unknown'), 'error');
    }
  }

  function onToggleVisible(field) {
    updateField(field.id, { is_visible: !field.is_visible });
  }

  function onDisplayNameChange(fieldId, value) {
    setEditing((prev) => ({ ...prev, [fieldId]: value }));
  }

  function onDisplayNameBlur(field) {
    const newValue = (editing[field.id] ?? field.display_name ?? '').trim();
    const currentValue = (field.display_name ?? '').trim();
    if (newValue === currentValue) {
      setEditing((prev) => {
        const next = { ...prev };
        delete next[field.id];
        return next;
      });
      return;
    }
    updateField(field.id, { display_name: newValue || null });
    setEditing((prev) => {
      const next = { ...prev };
      delete next[field.id];
      return next;
    });
  }

  async function handleHideAllCustom() {
    setBusyBulk(true);
    try {
      const res = await apiCall(
        `/connector-fields/configs/${configId}/fields/bulk`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            filter: { is_custom: true },
            set: { is_visible: false },
          }),
        }
      );
      const data = await res.json();
      showToast(`Hidden ${data.updated_count} custom field(s)`);
      await loadFields();
    } catch (err) {
      showToast(err.message || 'Bulk update failed', 'error');
    } finally {
      setBusyBulk(false);
    }
  }

  async function handleShowAll() {
    setBusyBulk(true);
    try {
      const res = await apiCall(
        `/connector-fields/configs/${configId}/fields/bulk`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            filter: { is_visible: false },
            set: { is_visible: true },
          }),
        }
      );
      const data = await res.json();
      showToast(`Restored ${data.updated_count} field(s)`);
      await loadFields();
    } catch (err) {
      showToast(err.message || 'Bulk update failed', 'error');
    } finally {
      setBusyBulk(false);
    }
  }

  // ---- filter by search query ----
  const lowerQuery = searchQuery.trim().toLowerCase();

  function matchesSearch(field) {
    if (!lowerQuery) return true;
    const haystack = [
      field.raw_field_id || '',
      field.raw_field_name || '',
      field.display_name || '',
      field.label || '',
      field.sample_value || '',
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(lowerQuery);
  }

  // ---- loading state ----
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-white">
              Field Mapping
            </h1>
            <p className="text-sm text-[#a0a0a0] mt-1">Loading fields...</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </div>
    );
  }

  // ---- error state ----
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-white">
              Field Mapping
            </h1>
            <p className="text-sm text-red-400 mt-1">{error}</p>
          </div>
        </div>
        <div className="text-center py-12">
          <p className="text-[#a0a0a0] mb-4">Failed to load fields</p>
          <Button onClick={loadFields}>
            <RefreshCw className="w-4 h-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // ---- main render ----
  const entityKeys = Object.keys(byEntity).sort();
  const totalFields = fields.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/connectors')}
            className="inline-flex items-center gap-1.5 text-xs text-[#a0a0a0] hover:text-white transition-colors mb-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Connectors
          </button>
          <h1 className="text-3xl font-semibold tracking-tight text-white">
            Field Mapping
          </h1>
          <p className="text-sm text-[#a0a0a0] mt-1">
            Customize how fields appear in dashboards and exports. Rename them,
            hide ones you do not need, and changes flow throughout Connex.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canEditConnector() && (
            <Button
              variant="secondary"
              onClick={handleDiscover}
              disabled={discovering}
            >
              <RefreshCw
                className={`w-4 h-4 ${discovering ? 'animate-spin' : ''}`}
              />
              {discovering ? 'Discovering...' : 'Re-discover'}
            </Button>
          )}
        </div>
      </div>

      {/* Toolbar: search + bulk actions */}
      {totalFields > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6a6a6a]" />
            <Input
              type="text"
              placeholder="Search fields..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          {canEditConnector() && (
            <>
              <Button
                variant="secondary"
                onClick={handleHideAllCustom}
                disabled={busyBulk}
              >
                <EyeOff className="w-4 h-4" />
                Hide all custom
              </Button>
              <Button
                variant="secondary"
                onClick={handleShowAll}
                disabled={busyBulk}
              >
                <Eye className="w-4 h-4" />
                Show all
              </Button>
            </>
          )}
        </div>
      )}

      {/* Empty state */}
      {totalFields === 0 ? (
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
          <EmptyState
            icon={RefreshCw}
            title="No fields discovered yet"
            description="Click Re-discover to fetch field metadata from the connector, or run a sync to capture fields from actual data."
            actionLabel="Re-discover fields"
            onAction={handleDiscover}
          />
        </div>
      ) : (
        entityKeys.map((entity) => {
          const entityFields = byEntity[entity].filter(matchesSearch);
          if (entityFields.length === 0) return null;

          return (
            <div key={entity} className="space-y-3">
              <div className="flex items-baseline justify-between">
                <h2 className="text-xs font-semibold tracking-wider uppercase text-[#a0a0a0]">
                  {entity}
                </h2>
                <span className="text-xs text-[#6a6a6a]">
                  {entityFields.length} of {byEntity[entity].length} field
                  {byEntity[entity].length === 1 ? '' : 's'}
                </span>
              </div>

              <TableWrapper>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[60px]">Visible</TableHead>
                      <TableHead className="w-[260px]">Display Name</TableHead>
                      <TableHead>Field ID</TableHead>
                      <TableHead className="w-[110px]">Type</TableHead>
                      <TableHead className="w-[90px]">Custom</TableHead>
                      <TableHead>Sample</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entityFields.map((field) => {
                      const liveValue =
                        editing[field.id] ?? field.display_name ?? '';
                      const placeholder =
                        field.raw_field_name || field.raw_field_id;
                      return (
                        <TableRow key={field.id}>
                          <TableCell>
                            <VisibilityToggle
                              visible={field.is_visible}
                              onClick={() => onToggleVisible(field)}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="text"
                              placeholder={placeholder}
                              value={liveValue}
                              onChange={(e) =>
                                onDisplayNameChange(field.id, e.target.value)
                              }
                              onBlur={() => onDisplayNameBlur(field)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') e.target.blur();
                                if (e.key === 'Escape') {
                                  setEditing((prev) => {
                                    const next = { ...prev };
                                    delete next[field.id];
                                    return next;
                                  });
                                  e.target.blur();
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <code className="text-xs text-[#a0a0a0] font-mono">
                              {field.raw_field_id}
                            </code>
                          </TableCell>
                          <TableCell>
                            {field.data_type ? (
                              <span className="text-xs text-[#a0a0a0]">
                                {field.data_type}
                              </span>
                            ) : (
                              <span className="text-xs text-[#6a6a6a]">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {field.is_custom ? (
                              <Badge variant="info">Custom</Badge>
                            ) : (
                              <span className="text-xs text-[#6a6a6a]">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {field.sample_value ? (
                              <div
                                className="text-xs text-[#a0a0a0] truncate max-w-[300px]"
                                title={field.sample_value}
                              >
                                {field.sample_value}
                              </div>
                            ) : (
                              <span className="text-xs text-[#6a6a6a]">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableWrapper>
            </div>
          );
        })
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg z-50 border ${
            toast.kind === 'error'
              ? 'bg-red-500/10 border-red-500/30 text-red-300'
              : 'bg-green-500/10 border-green-500/30 text-green-300'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

// ----- Visibility toggle -----
function VisibilityToggle({ visible, onClick }) {
  const canEdit = canEditConnector();
  return (
    <button
      type="button"
      onClick={canEdit ? onClick : undefined}
      role="switch"
      aria-checked={visible}
      disabled={!canEdit}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/40 ${
        visible ? 'bg-blue-500' : 'bg-[#2a2a2a]'
      } ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
          visible ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </button>
  );
}
